/**
 * Wrapped API Service - Handles HTTP requests for wrapped generation
 *
 * Single Responsibility: API communication for wrapped content generation
 */
import { apiClient, type ApiResult } from './apiClient'
import { sanitizeRequestBody, sanitizeName } from '../utils/sanitization'
import type {
  HoroscopeData,
  WordsData,
  VisionData,
  PhotoMemoriesData,
  YearReviewData,
  Intention,
  ThemeDiscoveryData,
  ThemeSynthesis,
  VisionSynthesis,
  UserProfile,
} from '../types'
import { WRAPPED_TARGET_YEAR } from '../constants/api'
import { selectTarotCard } from '../utils/seededRandom'
import { MAJOR_ARCANA } from '../constants/tarot'
import type {
  WrappedHoroscope,
  WrappedPhotoAnalysis,
  WrappedNarrative,
  WrappedIntention,
  WrappedTheme,
  WrappedConnectingThreads,
} from '../store/useWrappedStore'

export interface GenerateWrappedInput {
  name: string
  userId: number | null
  profile: UserProfile | null
  horoscope: HoroscopeData | null
  theme: ThemeDiscoveryData | null
  words: WordsData | null
  vision: VisionData | null
  photos: PhotoMemoriesData | null
  yearReview: YearReviewData | null
  intentions: Intention[]
}

interface WrappedApiResponse {
  horoscope?: WrappedHoroscope
  photos?: WrappedPhotoAnalysis
  narrative?: WrappedNarrative
  intentions?: WrappedIntention[]
  theme?: WrappedTheme
  themeSynthesis?: ThemeSynthesis
  visionSynthesis?: VisionSynthesis
  tarot?: {
    interpretation: string
    advice: string
  }
  connectingThreads?: WrappedConnectingThreads
}

export interface GenerateWrappedResult {
  horoscope: WrappedHoroscope | null
  photos: WrappedPhotoAnalysis | null
  narrative: WrappedNarrative | null
  intentions: WrappedIntention[]
  theme: WrappedTheme | null
  themeSynthesis: ThemeSynthesis | null
  visionSynthesis: VisionSynthesis | null
  tarot: {
    cardIndex: number
    isReversed: boolean
    interpretation: string
    advice: string
  }
  connectingThreads: WrappedConnectingThreads | null
}

/**
 * Build request body with tarot card selection and input sanitization
 */
function buildRequestBody(input: GenerateWrappedInput) {
  const { cardIndex, isReversed } = selectTarotCard(input.userId, input.name, WRAPPED_TARGET_YEAR)
  const selectedCard = MAJOR_ARCANA[cardIndex]

  // Sanitize user-provided text to prevent prompt injection
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sanitizedInput = sanitizeRequestBody(input as any) as GenerateWrappedInput
  const sanitizedName = sanitizeName(input.name)

  return {
    ...sanitizedInput,
    name: sanitizedName,
    tarot: {
      cardIndex,
      isReversed,
      cardName: selectedCard.name,
      baseKeywords: selectedCard.keywords,
      baseMeaning: isReversed ? selectedCard.reversedMeaning : selectedCard.uprightMeaning,
    },
  }
}

/**
 * Transform API response to wrapped result
 */
function transformResponse(
  data: WrappedApiResponse,
  cardIndex: number,
  isReversed: boolean
): GenerateWrappedResult {
  return {
    horoscope: data.horoscope || null,
    photos: data.photos || null,
    narrative: data.narrative || null,
    intentions: data.intentions || [],
    theme: data.theme || null,
    themeSynthesis: data.themeSynthesis || null,
    visionSynthesis: data.visionSynthesis || null,
    tarot: {
      cardIndex,
      isReversed,
      interpretation: data.tarot?.interpretation || '',
      advice: data.tarot?.advice || '',
    },
    connectingThreads: data.connectingThreads || null,
  }
}

/**
 * Generate wrapped content via API
 */
export async function generateWrappedContent(
  input: GenerateWrappedInput
): Promise<ApiResult<GenerateWrappedResult>> {
  const requestBody = buildRequestBody(input)
  const { cardIndex, isReversed } = requestBody.tarot

  const result = await apiClient.postAI<WrappedApiResponse>(
    '/api/generate-wrapped',
    requestBody
  )

  if (!result.success) {
    return result
  }

  return {
    success: true,
    data: transformResponse(result.data, cardIndex, isReversed),
  }
}
